/**
 * Orbit's "gray" — the canonical muted-text color across Orbit surfaces.
 *
 * Per the Orbit typography & color canon: "'gray' is Black at 0.55 opacity
 * — never a hex gray token." Same hue as Body Black, dialed down. The
 * canon docs (typography-and-color.mdx, date-time.mdx, activity-timeline.mdx,
 * inbox.mdx) name this value "Orbit's gray" — keeping the constant name
 * canon-faithful makes the doc ↔ code link greppable.
 *
 * Used for:
 *   - Field labels in content-tables ("gray label : Black content")
 *   - The `orbit-meta` typography variant (12/16/400) — relative dates,
 *     breadcrumbs, helper text, timestamps
 *   - Read message previews (vs full Black for unread)
 *   - Default-state action icons in card headers
 *   - Empty-state and "no records" placeholder text
 *
 * Distinct from Mantine's default `c="dimmed"` (gray-6 based) — the canon
 * explicitly migrated away from hex-gray tokens because they desync from
 * Black under different backgrounds / lighting.
 *
 * When the Phase 1 Mantine theme override lands, this should become a
 * proper theme token (e.g. `var(--mantine-color-orbit-gray)`) so the
 * single source of truth lives inside the theme.
 */
export const ORBIT_GRAY = 'rgba(0, 0, 0, 0.55)';

/**
 * Status colours for Orbit's operator surfaces: green means settled, amber
 * means needs-action.
 *
 * Per the Orbit canon these two are the *only* semantic hues on a
 * label-value panel or a billing card — a third categorical colour beside them
 * (an ESG-blue / SIS-purple offering pill, say) dilutes the signal, which is why
 * the offering pills live on the list views instead.
 *
 * Previously redeclared in `PaymentOrderCard`, `PaymentEventCard` and inline in
 * four Patient Access components; two of the copies had drifted to lowercase hex.
 */
export const ORBIT_SETTLED = '#1F7A44';
export const ORBIT_NEEDS_ACTION = '#8A5A12';

/**
 * Black at 0.35 — one step fainter than {@link ORBIT_GRAY}.
 *
 * For a value that is *legitimately* nothing yet, as distinct from a muted
 * label: `Not scheduled` on a call row is a real answer, not missing data, and
 * dimming it keeps the eye on the rows that have happened.
 */
export const ORBIT_DISABLED = 'rgba(0, 0, 0, 0.35)';

/**
 * The Status badge's five tones (design system `03-elements/components/badge`), for the payment
 * status (BAR-3084): a soft tint behind the status colour.
 */
export const STATUS_BADGE_TONES = {
  neutral: { background: 'rgba(0, 0, 0, 0.06)', color: '#000000' },
  info: { background: 'rgba(49, 128, 227, 0.12)', color: '#1A2273' },
  warning: { background: 'rgba(216, 150, 20, 0.16)', color: '#8A5D05' },
  success: { background: 'rgba(31, 143, 77, 0.14)', color: '#1F8F4D' },
  error: { background: 'rgba(201, 32, 26, 0.1)', color: '#C9201A' },
} as const;

/** A receivable's Type badge (BAR-3038): the consultation fee is teal so it never reads as a procedure invoice. */
export const RECEIVABLE_KIND_TONES = {
  consultationFee: {
    background: 'rgba(61, 139, 139, 0.12)',
    color: '#2A6B6B',
    boxShadow: 'inset 0 0 0 1px rgba(61, 139, 139, 0.35)',
  },
  procedure: { background: 'rgba(0, 0, 0, 0.05)', color: 'rgba(0, 0, 0, 0.55)', boxShadow: 'none' },
} as const;
